import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  EnvelopeIcon,
  RobotIcon,
  PaperPlaneRightIcon,
  CircleNotchIcon,
  AtIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { EmailDraftData } from './types';

interface EmailDraftSheetProps {
  draft: EmailDraftData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (draft: EmailDraftData) => void | Promise<void>;
  isSending?: boolean;
}

export function EmailDraftSheet({
  draft,
  open,
  onOpenChange,
  onSend,
  isSending = false,
}: EmailDraftSheetProps) {
  const gt = useGT();
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc || '');
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);

  // Update local state when draft prop changes (e.g. summary → full draft)
  // This avoids needing a key-based remount which triggers Sheet close/open animations
  const [prevDraft, setPrevDraft] = useState(draft);
  if (draft !== prevDraft) {
    setPrevDraft(draft);
    setTo(draft.to);
    setCc(draft.cc || '');
    setSubject(draft.subject);
    setBody(draft.body);
  }

  function handleSend() {
    onSend({ to, cc: cc || undefined, subject, body });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="z-[1600] flex w-full flex-col gap-0 p-0 sm:max-w-xl [&>button.absolute]:hidden"
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
        // Base UI's spelling of Radix's `onOpenAutoFocus` + `preventDefault`.
        initialFocus={false}
      >
        {/* Header */}
        <SheetHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 dark:from-blue-950/30 dark:to-indigo-950/30">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
                <EnvelopeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span>
                <T context="sheet title, review AI email draft">
                  Review Email Draft
                </T>
              </span>
            </SheetTitle>
            <Badge
              variant="secondary"
              className="gap-1.5 bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300"
            >
              <RobotIcon className="h-3 w-3" />
              <T context="badge, AI-generated email draft">AI Draft</T>
            </Badge>
          </div>
        </SheetHeader>

        {/* Email Form */}
        <div className="flex flex-1 flex-col overflow-y-auto">
          {/* Recipients */}
          <div className="space-y-0 border-b bg-gray-50/50 dark:bg-gray-900/20">
            <div className="flex items-center border-b border-gray-100 px-6 py-2 dark:border-gray-800">
              <div className="flex w-16 shrink-0 items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <AtIcon className="h-3.5 w-3.5" />
                <T context="email field label To">To</T>
              </div>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={gt('recipient@example.com', {
                  context: 'placeholder example email address',
                })}
                disabled={isSending}
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              />
            </div>

            <div className="flex items-center border-b border-gray-100 px-6 py-2 dark:border-gray-800">
              <div className="flex w-16 shrink-0 items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <UsersIcon className="h-3.5 w-3.5" />
                <T context="email field label Cc">Cc</T>
              </div>
              <Input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder={gt('Add recipients...', {
                  context: 'placeholder for CC recipients',
                })}
                disabled={isSending}
                className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
              />
            </div>

            <div className="flex items-center px-6 py-2">
              <div className="w-16 shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
                <T context="email subject field label">Subject</T>
              </div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={gt('Email subject', {
                  context: 'placeholder for email subject line',
                })}
                disabled={isSending}
                className="border-0 bg-transparent px-0 font-medium shadow-none focus-visible:ring-0"
              />
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 px-6 py-4">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={gt('Write your message...', {
                context: 'placeholder for email body',
              })}
              disabled={isSending}
              className="h-full min-h-[300px] flex-1 resize-none border-0 bg-transparent p-0 text-[15px] leading-relaxed shadow-none placeholder:text-gray-400 focus-visible:ring-0"
            />
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="flex-row items-center justify-between border-t bg-gray-50 px-6 py-4 dark:bg-gray-900/20">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
            size="sm"
            className="text-gray-500 hover:text-gray-700"
          >
            <T context="button, close email draft sheet">Close</T>
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending}
            size="sm"
            className="bg-blue-600 px-4 hover:bg-blue-700"
          >
            {isSending ? (
              <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <PaperPlaneRightIcon className="mr-1.5 h-3.5 w-3.5" />
            )}
            <T context="button, send email">Send</T>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
